<template>
   <div data-cy="profile-names-block" class="form-min-heights">
      <RequestLoader :isLoading="state.isFetchingData">
         <TextBox
            id="full-name"
            v-model.trim="state.input.fullName"
            :label="$t('user-data.fullname')"
            :maxLength="300"
            :hasError="showBlockErrors && $v.input.fullName.$error"
            :errorHelper="fullNameHelperData"
            @change="processInputs"
         />
         <div class="mx-auto mt-6 pb-2 lg:mt-10 lg:pb-6">
            <h2 v-if="props.block.data.preferredNameTitle" class="blockQuestionTitle">
               {{ props.block.data.preferredNameTitle }}
            </h2>
            <p v-if="props.block.data.preferredNameSubtitle" class="blockQuestionSubtitle">
               {{ props.block.data.preferredNameSubtitle }}
            </p>
         </div>
         <TextBox
            id="pref-name"
            v-model.trim="state.input.preferredName"
            :label="$t('user-data.preferred_name')"
            :maxLength="300"
            :hasError="showBlockErrors && $v.input.preferredName.$error"
            :errorHelper="prefNameHelperData"
            @change="processInputs"
         />
      </RequestLoader>
   </div>
</template>

<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { required, maxLength, helpers } from "@vuelidate/validators";
import { useI18n } from "@/vue-i18n/vue-i18n";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import type { AnswerResp } from "@/data/api-layer/answersApi";
import { useUserStore } from "@/stores/userStore";

export interface ProfileNamesAnswerData {
   fullName: string;
   preferredName: string;
}
export interface ProfileNamesBlock extends FlowselBlock {
   data: {
      preferredNameTitle: string;
      preferredNameSubtitle: string;
   };
}
interface Props {
   block: ProfileNamesBlock;
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}

// declarations
// -----------------------------------------
const { t: $t } = useI18n();
const emit = defineEmits(["update-valid", "update-answer"]);
const userStore = useUserStore();
const props = defineProps<Props>();

const state = reactive({
   isFetchingData: false,
   input: {
      fullName: "",
      preferredName: "",
   },
});

// Vuelidate & rules
// -----------------------------------------
const rules = {
   input: {
      fullName: {
         $autoDirty: true,
         ...(props.block.isRequired && { required: helpers.withMessage($t("validation.field_required"), required) }),
         maxLength: helpers.withMessage($t("validation.address.invalid_length"), maxLength(150)),
      },
      preferredName: {
         $autoDirty: true,
         ...(props.block.isRequired && { required: helpers.withMessage($t("validation.field_required"), required) }),
         maxLength: helpers.withMessage($t("validation.address.invalid_length"), maxLength(150)),
      },
   },
};
const $v: any = useVuelidate(rules, state);

// computed
//-----------------------------------------------
const fullNameHelperData = computed(() => {
   if ($v.value.input.fullName.$errors.length === 0) return null;
   return {
      text: $v.value.input.fullName.$errors[0].$message,
      code: $v.value.input.fullName.$errors[0].$validator,
   };
});
const prefNameHelperData = computed(() => {
   if ($v.value.input.preferredName.$errors.length === 0) return null;
   return {
      text: $v.value.input.preferredName.$errors[0].$message,
      code: $v.value.input.preferredName.$errors[0].$validator,
   };
});

// methods
// -----------------------------------------
/*
   Profile Block
   If the UserBlockAnswer answer is NULL, show loader (no inputs) and try to fetch any user profile data (e.g. another flow answer) to populate fields
   If the block answer is NOT NULL, use this to try to populate fields
*/
/**
 * Check userBlockAnswer for a previous response before
 * attempting to use user data to pre-populate
 */
function checkUserBlockAnswer() {
   if (props.userBlockAnswer) {
      state.input = {
         fullName: props.userBlockAnswer.data?.fullName,
         preferredName: props.userBlockAnswer.data?.preferredName,
      };
      processInputs(); // now validate fields
   } else {
      state.isFetchingData = true;
      fetchUserData().finally(() => (state.isFetchingData = false));
   }
}
/**
 * Try and fetch user data that we might be able to use to pre-populate fields with
 */
const fetchUserData = async () => {
   try {
      await userStore.setUser();
      initialiseComponent({ fullName: userStore.user?.fullname, preferredName: userStore.user?.name });
   } catch (e) {
      console.error("Failed to fetch user data", e);
   }
};

/**
 * update the inputs with any retrieved profile data
 * @param {object} userData
 */
function initialiseComponent(userData) {
   if (userData.fullName) state.input.fullName = userData.fullName;
   if (userData.preferredName) state.input.preferredName = userData.preferredName;

   if (!userData.fullName && !userData.preferredName) {
      console.info("No profile data available for user");
   }
   // call processInputs on initialisation as the fields maybe have been pre-populated and now valid
   processInputs();
}

/**
 * validate input data before emitting answer or
 * emit an error if something is invalid
 */
/**
 * validate input data before emitting answer / error info
 */
function processInputs() {
   if (!$v.value.$dirty) $v.value.$touch();

   if ($v.value.$invalid || $v.value.$error) {
      // an input is invalid or has errors
      emit("update-valid", false);
   } else {
      // all inputs are valid
      emit("update-valid", true);

      let answer = null;
      if (state.input.fullName && state.input.preferredName) {
         answer = {
            full_name: state.input.fullName,
            preferred_name: state.input.preferredName,
         };
      }
      emit("update-answer", answer);
   }
}
// execute on created
checkUserBlockAnswer();
</script>

<style lang="scss" scoped>
/* It's worth setting these min heights because they make the appearance of this block after loading data much smoother */
.form-min-heights {
   min-height: 15.25rem;
}

@media (max-width: 47.9375rem) {
   .form-min-heights {
      min-height: 10rem;
   }
}
</style>
